<template>
  <div class="mt-4">
    <div class="d-flex justify-content-between align-items-center">
      <div>
        <h4>Users in {{ supergroup.name }} - {{ userCount }} users
            <button class="btn btn-sm" @click="$emit('close')" aria-label="Close">
                <i class="fa fa-times"></i>
            </button>
        </h4>
        <p>
          <span v-if="activePremiumToolkitCount > 0">
            ({{ activePremiumToolkitCount }} Active Premium Toolkit)
          </span>
          <span v-if="activePremiumPathwaysCount > 0">
            ({{ activePremiumPathwaysCount }} Active Premium Pathways)
          </span>
        </p>
      </div>

    </div>
    <div v-if="loading" class="text-center">
      <i class="fa fa-spinner fa-spin fa-2x"></i>
    </div>
    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>
    <div v-else>
      <input type="text" class="form-control" v-model="filterText" placeholder="Filter by name or email...">
    </div>
    <div v-if="users.length === 0" class="text-muted">
      No users found in this supergroup.
    </div>
    <div v-else-if="sortedUsers.length === 0" class="text-muted">
      No users match your filter.
    </div>
    <div v-else>
      <table class="table table-striped table-condensed">
        <thead>
            <tr>
            <th @click="sortBy('name')" class="sortable">
                Name 
                <i v-if="sortKey === 'name'" :class="['fa', sortAsc ? 'fa-sort-asc' : 'fa-sort-desc']"></i>
            </th>
            <th @click="sortBy('email')" class="sortable">
                Email
                <i v-if="sortKey === 'email'" :class="['fa', sortAsc ? 'fa-sort-asc' : 'fa-sort-desc']"></i>
            </th>
            <th @click="sortBy('title')" class="sortable">
                Title
                <i v-if="sortKey === 'title'" :class="['fa', sortAsc ? 'fa-sort-asc' : 'fa-sort-desc']"></i>
            </th>
            <th @click="sortBy('active_toolkit_license.end_date')" class="sortable">
                Toolkit End Date
                <i v-if="sortKey === 'active_toolkit_license.end_date'" :class="['fa', sortAsc ? 'fa-sort-asc' : 'fa-sort-desc']"></i>
            </th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="user in sortedUsers" :key="user.uuid">
            <td><a :href="'#!/user_info?uuid=' + user.uuid">{{ user.name }}</a></td>
            <td>{{ user.email }}</td>
            <td>{{ user.title }}</td>
            <td>{{ formatEndDate(user.active_toolkit_license && user.active_toolkit_license.end_date) }}</td>
            </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SupergroupUsers',
  props: {
    supergroup: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      users: [],
      error: null,
      filterText: '',
      sortKey: 'name',
      sortAsc: true,
    };
  },
  computed: {
    userCount() {
      return this.sortedUsers.length;
    },
    filteredUsers() {
      if (!this.filterText) {
        return this.users;
      }
      const lowerCaseFilter = this.filterText.toLowerCase();
      return this.users.filter(user => 
        (user.name && user.name.toLowerCase().includes(lowerCaseFilter)) ||
        (user.email && user.email.toLowerCase().includes(lowerCaseFilter))
      );
    },
    sortedUsers() {
      let users = [...this.filteredUsers];

      // Sorting
      if (this.sortKey) {
        const getNestedValue = (obj, path) => path.split('.').reduce((acc, part) => acc && acc[part], obj);

        users.sort((a, b) => {
          let valA = getNestedValue(a, this.sortKey);
          let valB = getNestedValue(b, this.sortKey);

          // Handle null or undefined values for sorting
          if (valA == null) return 1;
          if (valB == null) return -1;

          if (this.sortKey.includes('date')) {
            const dateA = new Date(valA);
            const dateB = new Date(valB);
            return this.sortAsc ? dateA - dateB : dateB - dateA;
          }

          if (typeof valA === 'string' && typeof valB === 'string') {
            valA = valA.toLowerCase();
            valB = valB.toLowerCase();
          }

          if (valA < valB) return this.sortAsc ? -1 : 1;
          if (valA > valB) return this.sortAsc ? 1 : -1;
          return 0;
        });
      }

      return users;
    },
    activePremiumToolkitCount() {
      return this.sortedUsers.filter(user => user.active_toolkit_license?.sub_type === 'premium' && user.active_toolkit_license?.type === 'enterprise' && new Date(user.active_toolkit_license?.end_date) >= new Date()).length;
    },
    activePremiumPathwaysCount() {
      return this.sortedUsers.filter(user => user.active_pathways_license?.sub_type === 'premium' && user.active_pathways_license?.type === 'enterprise' && new Date(user.active_pathways_license?.end_date) >= new Date()).length;
    }
  },
  watch: {
    supergroup: {
      immediate: true,
      handler(newSupergroup) {
        if (newSupergroup && newSupergroup.uuid) {
          this.fetchUsers();
        }
      },
    },
  },
  methods: {
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortKey = key;
        this.sortAsc = true;
      }
    },
    formatEndDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString();
    },
    async fetchUsers() {
      if (!this.supergroup || !this.supergroup.uuid) return;

      this.loading = true;
      this.error = null;
      try {
        const response = await this.$angularWorld.adminEntitiesApi.fetchNormalizedUsersInSupergroup(this.supergroup.uuid);
        this.users = response.data;
      } catch (err) {
        console.error(`Failed to fetch users for supergroup ${this.supergroup.uuid}:`, err);
        this.error = 'Failed to load users. Please try again.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
h5 {
  font-size: 1rem;
  color: #6c757d;
}
.sortable {
  cursor: pointer;
}
.sortable:hover {
  background-color: #f5f5f5;
}
.btn-sm {
    float: right;
}
input {
    margin-bottom: 12px;
}
</style>
